import { useState, useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { patientAuthService, Patient, PatientSignupData, PatientLoginData } from '@lib/supabase/patientAuth.service'
import { useAuth } from '@contexts/AuthContext'
import { validateEmail, validatePassword, validatePhoneNumber } from '@lib/validations'

interface UsePatientAuthReturn {
  patient: Patient | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null

  login: (data: PatientLoginData) => Promise<{ success: boolean; error?: string; redirectTo?: string }>
  signup: (data: PatientSignupData) => Promise<{ success: boolean; error?: string }>
  logout: () => Promise<void>
  verifyEmail: (email: string, token: string) => Promise<{ success: boolean; error?: string }>
  resendVerificationCode: (email: string) => Promise<{ success: boolean; error?: string }>
  refreshPatient: () => Promise<void>

  validateEmail: (email: string) => boolean
  validatePassword: typeof validatePassword
  validatePhoneNumber: (phone: string) => boolean
}

export function usePatientAuth(): UsePatientAuthReturn {
  const [error, setError] = useState<string | null>(null)
  const { session, role } = useAuth()
  const queryClient = useQueryClient()
  const patientProfileQueryKey = ['auth', 'patient-profile', session?.user.id ?? 'anonymous'] as const
  const isPatientProfileEnabled = !!session && role === 'patient'

  const refreshPatient = useCallback(async () => {
    if (!isPatientProfileEnabled) {
      queryClient.removeQueries({ queryKey: ['auth', 'patient-profile'] })
      return
    }

    await queryClient.invalidateQueries({ queryKey: patientProfileQueryKey })
  }, [isPatientProfileEnabled, patientProfileQueryKey, queryClient])

  const patientProfileQuery = useQuery({
    queryKey: patientProfileQueryKey,
    queryFn: patientAuthService.getProfile,
    enabled: isPatientProfileEnabled,
  })

  const patient = isPatientProfileEnabled ? patientProfileQuery.data ?? null : null
  const isLoading = isPatientProfileEnabled ? patientProfileQuery.isPending : false

  const login = useCallback(async (data: PatientLoginData) => {
    setError(null)
    const result = await patientAuthService.login(data)

    if (!result.success) {
      return { success: false, error: result.error }
    }

    if (result.patient) {
      queryClient.setQueryData(['auth', 'patient-profile', result.patient.auth_id], result.patient)
    }

    // Determine redirect based on verification status
    const profile = result.patient
    let redirectTo = '/patient/dashboard'

    if (!profile) {
      return { success: true, redirectTo }
    }

    if (!profile.email_verified) {
      redirectTo = `/auth/verify-email?email=${encodeURIComponent(data.email)}&role=patient`
    } else if (profile.verification_status === 'approved' || profile.verification_status === 'verified') {
      redirectTo = '/patient/dashboard'
    } else if (profile.verification_status === 'pending') {
      redirectTo = '/auth/verification-pending'
    } else {
      // null or 'rejected' → re-verify identity
      redirectTo = '/auth/verify-identity'
    }

    return { success: true, redirectTo }
  }, [queryClient])

  const signup = useCallback(async (data: PatientSignupData) => {
    setError(null)
    const result = await patientAuthService.signup(data)
    if (!result.success) {
      return { success: false, error: result.error }
    }
    return { success: true }
  }, [])

  const logout = useCallback(async () => {
    await patientAuthService.logout()
    queryClient.removeQueries({ queryKey: ['auth', 'patient-profile'] })
  }, [queryClient])

  const verifyEmail = useCallback(async (email: string, token: string) => {
    const result = await patientAuthService.verifyEmail(email, token)
    if (result.success) {
      await refreshPatient()
    }
    return result
  }, [refreshPatient])

  const resendVerificationCode = useCallback(async (email: string) => {
    return patientAuthService.resendVerificationEmail(email)
  }, [])

  return {
    patient,
    isAuthenticated: !!patient,
    isLoading,
    error,
    login,
    signup,
    logout,
    verifyEmail,
    resendVerificationCode,
    refreshPatient,
    validateEmail,
    validatePassword,
    validatePhoneNumber,
  }
}
